import { useState, useEffect } from 'react';
import { GOLD, MONO, LIGHT, MUTE } from '../lib/constants';
import { Dp, Bd, Mo } from './shared';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * PaymentSourceLinkModal
 *
 * Web modal for the Payment Source Linking flow.
 * Steps: pick account → scanning → pick candidate → confirm → success
 *
 * Props:
 *   debt       — debt object (needs debt.id, debt.name)
 *   onClose    — called when user dismisses (without linking)
 *   onLinked   — called after successful link
 */
export default function PaymentSourceLinkModal({ debt, onClose, onLinked }) {
  const [linkStep, setLinkStep] = useState('pick'); // pick | scanning | candidates | none | success
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState([]); // multi-select array

  const toggleCandidate = (c) => {
    setSelected(prev => {
      const already = prev.some(s => s.signature === c.signature);
      return already ? prev.filter(s => s.signature !== c.signature) : [...prev, c];
    });
  };
  const [confirming, setConfirming] = useState(false);
  const [historicalCount, setHistoricalCount] = useState(0);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const res = await fetch(`${API}/api/plaid/accounts`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAccounts(data.accounts || []);
      }
    } catch (e) {
      console.error('Failed to load accounts:', e);
    }
  };

  const handlePickAccount = async (account) => {
    setSelectedAccount(account);
    setLinkStep('scanning');
    try {
      const res = await fetch(`${API}/api/debts/scan-payments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          account_id: account.account_id,
          debt_name: debt.name,
          statement_name: debt.statement_name || debt.name,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setCandidates(data.candidates || []);
        setLinkStep(data.candidates?.length > 0 ? 'candidates' : 'none');
      } else {
        setLinkStep('none');
      }
    } catch {
      setLinkStep('none');
    }
  };

  const handleConfirm = async () => {
    if (selected.length === 0 || !selectedAccount) return;
    setConfirming(true);
    try {
      const res = await fetch(`${API}/api/debts/confirm-payment-source`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          debt_id: debt.id,
          account_id: selectedAccount.account_id,
          account_name: selectedAccount.name,
          account_last4: selectedAccount.mask || '',
          reference_codes: selected.map(c => c.signature),
          reference_code: selected[0]?.signature,  // backwards compat
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setHistoricalCount(data.historical_payments_found || 0);
        setLinkStep('success');
        // Store matched payments so parent can display immediately without a second fetch
        if (onLinked && data.payments?.length) {
          // defer — let user read the success screen first, then pass on close
          window._pendingLinkedPayments = data.payments;
        }
      } else {
        alert('Could not save payment source. Try again.');
      }
    } catch {
      alert('An error occurred. Please try again.');
    } finally {
      setConfirming(false);
    }
  };

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  return (
    /* Backdrop */
    <div
      onClick={(e) => { if (e.target === e.currentTarget && linkStep !== 'success') onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 9000,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div style={{
        background: 'var(--base)',
        border: '1px solid var(--ghost)',
        borderRadius: 16,
        width: '100%', maxWidth: 440,
        maxHeight: '85vh',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>

        {/* Header */}
        <div style={{
          padding: '18px 20px 14px',
          borderBottom: '1px solid var(--faint)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span style={{ fontFamily: MONO, fontSize: 11, color: GOLD, letterSpacing: 1.5, fontWeight: 700 }}>
            LINK PAYMENTS
          </span>
          {linkStep !== 'success' && (
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', color: MUTE, cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 4 }}
            >
              ✕
            </button>
          )}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>

          {/* ── pick: choose account ── */}
          {linkStep === 'pick' && (
            <>
              <p style={{ fontFamily: MONO, fontSize: 12, color: MUTE, margin: '0 0 4px', letterSpacing: 0.5 }}>
                Which account do you pay{' '}
                <strong style={{ color: LIGHT }}>{debt.name}</strong> from?
              </p>
              <p style={{ fontFamily: MONO, fontSize: 11, color: 'var(--ghost)', margin: '0 0 20px' }}>
                We'll scan transactions and track payments automatically.
              </p>

              {accounts.length === 0 ? (
                <div style={{ padding: 16, border: '1px solid var(--ghost)', borderRadius: 10, textAlign: 'center' }}>
                  <p style={{ fontFamily: MONO, fontSize: 12, color: MUTE, margin: 0 }}>
                    No bank accounts connected yet.
                    Connect a bank in Settings to enable payment tracking.
                  </p>
                </div>
              ) : (
                accounts.map(acc => (
                  <button
                    key={acc.account_id}
                    onClick={() => handlePickAccount(acc)}
                    data-testid={`link-account-${acc.account_id}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      width: '100%', textAlign: 'left',
                      padding: '14px 16px', marginBottom: 8,
                      background: 'var(--faint)', border: '1px solid var(--ghost)',
                      borderRadius: 10, cursor: 'pointer',
                      transition: 'border-color 0.15s',
                    }}
                    onMouseOver={e => e.currentTarget.style.borderColor = GOLD}
                    onMouseOut={e => e.currentTarget.style.borderColor = 'var(--ghost)'}
                  >
                    <span style={{ fontSize: 20 }}>🏦</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: MONO, fontSize: 13, color: LIGHT, fontWeight: 600 }}>{acc.name}</div>
                      <div style={{ fontFamily: MONO, fontSize: 11, color: MUTE, marginTop: 2 }}>
                        {acc.subtype || acc.type || 'Account'} ••••{acc.mask}
                      </div>
                    </div>
                    <span style={{ color: GOLD, fontSize: 18 }}>›</span>
                  </button>
                ))
              )}
            </>
          )}

          {/* ── scanning ── */}
          {linkStep === 'scanning' && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{ marginBottom: 20 }}>
                <svg width={36} height={36} viewBox="0 0 24 24" style={{ animation: 'spin 1s linear infinite' }}>
                  <circle cx="12" cy="12" r="10" fill="none" stroke="var(--faint)" strokeWidth="2" />
                  <circle cx="12" cy="12" r="10" fill="none" stroke={GOLD} strokeWidth="2" strokeDasharray="30 70" strokeLinecap="round" />
                </svg>
              </div>
              <p style={{ fontFamily: MONO, fontSize: 13, color: LIGHT, fontWeight: 700, marginBottom: 8 }}>
                Scanning transactions...
              </p>
              <p style={{ fontFamily: MONO, fontSize: 11, color: MUTE, margin: 0 }}>
                Looking for payments to {debt.name} in {selectedAccount?.name}
              </p>
            </div>
          )}

          {/* ── candidates ── */}
          {linkStep === 'candidates' && (
            <>
              <p style={{ fontFamily: MONO, fontSize: 12, color: MUTE, margin: '0 0 4px' }}>
                Which payments belong to <strong style={{ color: LIGHT }}>{debt.name}</strong>?
              </p>
              <p style={{ fontFamily: MONO, fontSize: 11, color: 'var(--ghost)', margin: '0 0 20px' }}>
                Select all amounts that match this card — you can pick multiple.
              </p>

              {candidates.map(c => {
                const isSelected = selected.some(s => s.signature === c.signature);
                return (
                  <button
                    key={c.signature}
                    onClick={() => toggleCandidate(c)}
                    data-testid={`link-candidate-${c.signature}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      width: '100%', textAlign: 'left',
                      padding: '14px 16px', marginBottom: 8,
                      background: isSelected ? '#1a1500' : 'var(--faint)',
                      border: `1px solid ${isSelected ? GOLD : 'var(--ghost)'}`,
                      borderRadius: 10, cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <Dp style={{ fontSize: 20, fontWeight: 800, color: isSelected ? GOLD : LIGHT, display: 'block', marginBottom: 4 }}>
                        ${c.most_recent_amount.toFixed(2)}
                      </Dp>
                      {c.display_description && (
                        <Bd style={{ fontSize: 12, color: GOLD, display: 'block', marginBottom: 4, letterSpacing: 0.3 }}>
                          {c.display_description}
                        </Bd>
                      )}
                      <Mo style={{ fontSize: 11, color: MUTE, display: 'block' }}>
                        Last payment {formatDate(c.most_recent_date)}
                        {c.occurrence_count > 1 ? ` · ${c.occurrence_count} found` : ''}
                      </Mo>
                    </div>
                    {/* Checkbox indicator */}
                    <div style={{
                      width: 22, height: 22, borderRadius: 4,
                      border: `2px solid ${isSelected ? GOLD : 'var(--ghost)'}`,
                      background: isSelected ? GOLD : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      transition: 'all 0.15s',
                    }}>
                      {isSelected && (
                        <span style={{ color: '#000', fontSize: 13, fontWeight: 800, lineHeight: 1 }}>✓</span>
                      )}
                    </div>
                  </button>
                );
              })}

              <button
                onClick={() => setLinkStep('none')}
                style={{ width: '100%', textAlign: 'center', padding: '10px 0', background: 'none', border: 'none', fontFamily: MONO, fontSize: 11, color: MUTE, cursor: 'pointer', marginTop: 4 }}
              >
                None of these look right
              </button>

              <button
                onClick={handleConfirm}
                disabled={selected.length === 0 || confirming}
                data-testid="link-confirm-btn"
                style={{
                  width: '100%', marginTop: 16, padding: '14px 0',
                  background: selected.length > 0 ? GOLD : 'var(--ghost)',
                  border: 'none', borderRadius: 10,
                  cursor: selected.length > 0 ? 'pointer' : 'not-allowed',
                  fontFamily: MONO, fontSize: 12, fontWeight: 700, letterSpacing: 1,
                  color: selected.length > 0 ? '#000' : MUTE,
                  transition: 'all 0.15s',
                }}
              >
                {confirming
                  ? 'LINKING...'
                  : selected.length === 0
                    ? 'SELECT PAYMENTS'
                    : `CONFIRM ${selected.length} PAYMENT${selected.length > 1 ? 'S' : ''} →`}
              </button>
            </>
          )}

          {/* ── none ── */}
          {linkStep === 'none' && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <p style={{ fontFamily: MONO, fontSize: 13, color: LIGHT, fontWeight: 700, marginBottom: 12 }}>
                None selected
              </p>
              <p style={{ fontFamily: MONO, fontSize: 11, color: MUTE, lineHeight: 1.6, marginBottom: 20 }}>
                No problem. You can go back and review the candidates again, or close and try a different bank account.
              </p>
              {candidates.length > 0 && (
                <button
                  onClick={() => { setSelected([]); setLinkStep('candidates'); }}
                  data-testid="link-back-to-candidates-btn"
                  style={{
                    width: '100%', padding: '14px 0', background: '#1a1400',
                    border: `1px solid ${GOLD}40`, borderRadius: 10, cursor: 'pointer',
                    fontFamily: MONO, fontSize: 12, fontWeight: 700, letterSpacing: 1, color: GOLD,
                    marginBottom: 10,
                  }}
                >
                  ← BACK TO CANDIDATES
                </button>
              )}
              <button
                onClick={() => setLinkStep('pick')}
                data-testid="link-try-different-account-btn"
                style={{
                  width: '100%', padding: '14px 0', background: 'var(--faint)',
                  border: '1px solid var(--ghost)', borderRadius: 10, cursor: 'pointer',
                  fontFamily: MONO, fontSize: 12, fontWeight: 700, letterSpacing: 1, color: MUTE,
                  marginBottom: 10,
                }}
              >
                TRY A DIFFERENT ACCOUNT
              </button>
              <button
                onClick={onClose}
                style={{
                  width: '100%', padding: '10px 0', background: 'none',
                  border: 'none', cursor: 'pointer',
                  fontFamily: MONO, fontSize: 11, color: 'var(--ghost)',
                }}
              >
                Close
              </button>
            </div>
          )}

          {/* ── success ── */}
          {linkStep === 'success' && (
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <div style={{ fontSize: 40, marginBottom: 16 }}>✓</div>
              <p style={{ fontFamily: MONO, fontSize: 14, color: LIGHT, fontWeight: 700, marginBottom: 8 }}>
                Payment connected
              </p>
              <p style={{ fontFamily: MONO, fontSize: 11, color: MUTE, lineHeight: 1.6, marginBottom: 16 }}>
                Every payment to <strong style={{ color: LIGHT }}>{debt.name}</strong> from{' '}
                <strong style={{ color: LIGHT }}>{selectedAccount?.name}</strong> will now be
                tracked automatically.
              </p>
              {historicalCount > 0 && (
                <div style={{
                  background: '#071407', border: '1px solid #1a3a1a', borderRadius: 10,
                  padding: '12px 16px', marginBottom: 16,
                }}>
                  <div style={{ fontFamily: MONO, fontSize: 13, color: '#4ade80', fontWeight: 700, marginBottom: 4 }}>
                    {historicalCount} past payment{historicalCount !== 1 ? 's' : ''} imported
                  </div>
                  <div style={{ fontFamily: MONO, fontSize: 11, color: '#5a8a5a' }}>
                    Your payment history is now up to date.
                  </div>
                </div>
              )}
              <button
                onClick={() => {
                  const linked = window._pendingLinkedPayments || [];
                  window._pendingLinkedPayments = null;
                  onLinked(linked);
                }}
                data-testid="link-done-btn"
                style={{
                  width: '100%', padding: '14px 0', background: GOLD,
                  border: 'none', borderRadius: 10, cursor: 'pointer',
                  fontFamily: MONO, fontSize: 12, fontWeight: 700, letterSpacing: 1, color: '#000',
                }}
              >
                DONE →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
